// Interface (WS-49): puts the visitor controls (js/chrome/, one component per control id of the settings list) into
// the slots of the preset's layout and keeps them in step with the runtime. A control is on the page only while the
// preset shows it (removed, never hidden by CSS); a container with nothing shown in it is not drawn; what is shown,
// the layout, the language, labels and card size all apply live (runtime 'change'). The default layout with today's
// implicit preset draws exactly the interface main had. Data shapes and hooks: js/chrome/README.md.
import { html } from './dom.js';
import { BOOT, MODES, qa } from './config.js';
import { i18n, loadStrings } from './i18n.js';
import { renderGallery } from './gallery.js'; // track T5: the card's two hooks, nothing else of theirs
import { bookingButton } from './booking.js';
import { createContext } from './chrome/context.js';
import { createPage } from './chrome/page.js';
import { mapLabels } from './chrome/labels.js';
import { sweep } from './chrome/popup.js';
import { controlRects, controlOf, controlAt, markGhosts, outline } from './chrome/editor.js'; // WS-51: the editor's affordances; VIS-73 controlAt
import { tourScreen } from './chrome/tourscreen.js'; // owner's pick Q5 A (2026-10-31): only the words while a tour plays

await loadStrings('en'); // English is the last fallback of every text: nothing is drawn before it is there
// WS-60: a map style other than Night Glass (BOOT.style) is fetched only by its maps: its CSS is in before the first draw
const STYLE = BOOT.style ? (await import('./lazy/style.js').catch(Object)).default : null; // a failed fetch: Night Glass

const DEBUG = qa('debug') !== null; // a QA parameter: under a preset on draft hosts only (config.js)
// changes that move a value but never what is on the page (they can arrive every frame: a time-lapse, a tour)
const LIGHT = /^(time\.(hour|date)|aurora\.|look\.|camera\.|lights\.|trees\.|render\.)/;
// settings the interface applies by drawing the page again from the resolved values (build, on the runtime's 'change')
const DRAWN = ['time.dates', 'labels.*', 'labels.unitNumbers', 'labels.unitBadges', 'cards.size', 'layout.id', 'explore.collapsed', 'lang.mode', 'lang.offered', 'booking.show', 'interiors.show', 'interiors.walkIn', 'booking.actions',
  'places.scenery', 'places.named', 'places.marked']; // WS-55: the place levels (labels, list, search; main.js reads them for the picking)

export function createUI(api) {
  const body = document.body, chrome = document.querySelector('#chrome');
  const c = createContext(api, i18n, !BOOT.preset), rt = c.rt;
  // VIS-230: a phone (max-width 760 px) wears layout B (chrome/phone.js); turning the phone draws the page again
  const narrow = matchMedia('(max-width: 760px)'); // (the phone of chrome/phone.js phoneOn)
  c.narrow = narrow.matches; c.kiosk = MODES.includes('kiosk');

  // ---------------------------------------------------------------- toast (WS-4; text arguments, never markup)
  let toastTimer = 0;
  c.toast = (eyebrow, title, sub, ms = 2200) => {
    const el = document.querySelector('#toast');
    el.innerHTML = html`<span class="eyebrow">${eyebrow}</span><b>${title}</b><small>${sub}</small>`;
    el.classList.remove('show'); void el.offsetWidth; el.classList.add('show');
    clearTimeout(toastTimer); toastTimer = setTimeout(() => el.classList.remove('show'), ms);
  };

  // ---------------------------------------------------------------- the page: regions in document order
  const page = createPage(c, { renderGallery, bookingButton, debug: DEBUG });
  STYLE?.(page, c); // WS-60: the style lays its regions over the page's (no style: null)
  const { top, time, explore, column, lobby, card, phone } = page;
  const els = {}, keys = {};
  function place(name, markup) { // puts a region's element where it belongs in the page order (or takes it away)
    els[name]?.remove();
    const t = document.createElement('template');
    t.innerHTML = html`${markup}`;
    const el = (els[name] = t.content.firstElementChild);
    if (el) chrome.insertBefore(el, page.order.slice(page.order.indexOf(name) + 1).map((n) => els[n]).find((e) => e?.parentNode === chrome) || null); // (VIS-230: a phone's sheet may hold a region meanwhile)
    return el;
  }

  // ---------------------------------------------------------------- build: what is on the page now
  const labels = mapLabels(c);
  let selectedKey = null, cardKey = null, contentRev = 0, bodyClass = null, drawnFor = '';
  // ---- WS-51 editor mode (begin): the bridge's editor state as the interface follows it (bridge.js; a visitor's page
  // never gets one, so `ed` stays false and nothing below runs). Ghosts are planned by page.js and marked here; the
  // rectangles of the controls go to the editor whenever they have changed; the outline follows what is being edited.
  let ed = false, marked = false, wired = false, target = null, sent = '';
  const timers = [], subs = new Set(), menus = new Set(); // menus: VIS-73 a right press on a control in editor mode
  const rects = () => controlRects({ reg: c.reg, layout: c.layout, shown: new Set(c.shown()), drawn: c.drawn, ghosts: c.ghosts, doc: document });
  const targetRect = (list = rects()) => { const ctl = target && controlOf(c.reg, target); return (ctl && list.find((r) => r.id === ctl.id)?.rect) || null; };
  function settle() {
    if (!ed) return;
    const list = rects(), sig = JSON.stringify(list);
    outline(document, targetRect(list));
    if (sig !== sent) { sent = sig; for (const fn of subs) fn(list); }
  }
  // once the page has been drawn, and again when its panels have finished moving
  function report() { if (!ed) return; timers.splice(0).forEach(clearTimeout); timers.push(setTimeout(settle, 120), setTimeout(settle, 900)); }
  const ROWS = ['top-left', 'top-actions', 'dock']; // containers that are dimmed as a whole when all they hold is ghosts
  // ---- WS-51 editor mode (end)
  function build() {
    const regions = page.layout();
    if (!i18n.loaded(i18n.lang)) loadStrings(i18n.lang).then(build);
    document.documentElement.lang = i18n.lang;
    // everything but `show` that the markup is made from
    const rev = JSON.stringify([c.phone, i18n.lang, i18n.loaded(i18n.lang), c.layout.id, contentRev, c.v('brand.logo'), c.v('brand.showName'), c.v('time.dates'), c.v('season.offered'), c.v('lang.offered'), c.v('layers.list'), api.tours.list(), Object.entries(c.v() || {}).filter(([k]) => k.startsWith('x.'))]);
    let redrawn = false;
    for (const r of regions) {
      const key = `${rev}|${r.key}`;
      if (keys[r.name] === key) continue;
      keys[r.name] = key; redrawn = true;
      const el = place(r.name, r.html());
      if (el) for (const id of r.ids) page.PARTS[id].wire?.(el);
      r.region.wire?.(el);
    }
    sweep();
    if (redrawn && body.classList.contains('ready')) body.classList.add('chrome-live'); // later arrivals skip the staggered entrance
    const cls = /^[a-z][a-z0-9-]*$/.test(c.layout.bodyClass || '') ? c.layout.bodyClass : null; // the layout's look (lobby-map, lobby-panel)
    if (cls !== bodyClass) { if (bodyClass) body.classList.remove(bodyClass); if (cls) body.classList.add(cls); bodyClass = cls; }
    body.classList.toggle('cards-compact', c.v('cards.size') === 'compact');
    body.classList.toggle('no-top-left', !els['top-left']);
    explore.sync(); time.sync(); column.sync(); lobby.draw(); phone.sync();
    // labels, list and card: drawn again when their texts or their parts change
    const all = c.v() || {};
    const sig = JSON.stringify([rev, c.slots.card, c.slots.map, c.season, all['booking.show'], all['interiors.show'], all['interiors.walkIn'], Object.entries(all).filter(([k]) => k.startsWith('labels.'))]);
    if (sig !== drawnFor) { drawnFor = sig; labels.draw(); explore.draw(); card.refresh(cardKey); }
    if (c.ghostsOn || marked) { // WS-51: mark the ghosts (and clear the marks when the editor has left)
      marked = c.ghostsOn;
      markGhosts(c.reg, c.ghosts, document, regions.filter((r) => ROWS.includes(r.name) && r.ids.length && r.ids.every((id) => c.ghosts.has(id))).map((r) => els[r.name]).filter(Boolean));
    }
    report();
  }
  build();
  time.start(); column.start();
  narrow.onchange = (e) => { c.narrow = e.matches; build(); };

  // ---------------------------------------------------------------- live: the runtime and the tour engine
  for (const key of DRAWN) rt.register(key, () => {}); // their applier is build(), run by the 'change' below
  rt.on('ready', build);
  let shownNow = c.shown().join();
  rt.on('change', ({ keys: changed = [] } = {}) => {
    // what is shown may change together with a light value (a preset that hides the date picker also takes the
    // visitor's date away): the page is drawn again whenever the shown controls differ, whatever else changed
    const shown = c.shown().join(), same = shown === shownNow;
    shownNow = shown;
    if (same && changed.length && changed.every((k) => LIGHT.test(k))) {
      if (changed.includes('time.date')) time.sync();
      if (changed.some((k) => /^(aurora|trees|render)\./.test(k))) column.sync();
      phone.tick();
      return;
    }
    if (changed.includes('season.current')) time.setSeason(c.v('season.current'));
    if (changed.some((k) => /^(content|booking|places|categories)\./.test(k))) contentRev++; // WS-55: + the place levels and what is hidden (main.js has laid the content again)
    build();
  });
  // a tour plays: the page steps aside for its words, its progress line and its buttons (chrome/tourscreen.js)
  tourScreen(c);
  top.setTouring(!!api.tours.current());
  api.tours.on('start', () => top.setTouring(true));
  api.tours.on('stop', (e) => lobby.show(e));
  api.tours.on('end', () => { top.setTouring(false); lobby.show(null); });

  /** What a panel of the layout covers of the map, in px from each edge (null = nothing): main.js centres the
   *  picture in the part that stays visible. A panel along a side covers that side; a wide one (phones) the bottom.
   *  VIS-31: on a phone the open place card is that panel (it would cover the place the map just flew to). */
  const inset = () => {
    const el = c.layout.panel ? els.card : c.phone ? document.querySelector('#card.open') : null, r = el?.getBoundingClientRect();
    if (!r || !r.width || !r.height || r.top >= innerHeight) return null;
    const W = innerWidth, H = innerHeight, onLeft = r.left < W - r.right;
    return r.width < W * 0.6 ? { left: onLeft ? r.right : 0, right: onLeft ? 0 : W - r.left, top: 0, bottom: 0 } : { left: 0, right: 0, top: 0, bottom: H - r.top };
  };
  // VIS-31: a phone's card slides in, grows by its handle, steps aside under Settings or a tour: the picture follows
  // its edge (api.reinset = main.js applyInset) until it has stood still for half a second
  let insetAt = '', insetRaf = 0, insetStill = 0;
  const followInset = () => { insetStill = 0; if (!insetRaf && api.reinset && (c.phone || insetAt)) insetRaf = requestAnimationFrame(stepInset); };
  function stepInset() {
    insetRaf = 0;
    const m = c.phone ? inset() : null, s = m ? `${m.left | 0} ${m.right | 0} ${m.bottom | 0}` : '';
    if (s !== insetAt) { insetAt = s; insetStill = 0; api.reinset(); }
    if (++insetStill < 30) insetRaf = requestAnimationFrame(stepInset);
  }
  new MutationObserver(followInset).observe(body, { attributes: true, attributeFilter: ['class'] });

  return {
    toast: c.toast, // (eyebrow, title, sub, ms): main.js's Game mode hint
    hover: labels.hover,
    moveTooltip: labels.moveTooltip,
    /** `showCard` false (WS-51, editor mode): the place is selected and its visitor card stays closed. */
    select(p, showCard = true) { selectedKey = p?.id || null; cardKey = showCard ? selectedKey : null; card.render(showCard ? p : null); body.classList.toggle('has-selection', !!p); labels.select(selectedKey); report(); followInset(); },
    // ---- WS-51 editor mode (begin)
    /** The bridge's editor state: the affordances are on in editor mode and off in "Preview as visitor". */
    editor(s) {
      const on = !!s.editor && !s.preview, ghosts = on && !!s.ghosts, redraw = ghosts !== c.ghostsOn || on !== ed;
      ed = c.editing = on; c.ghostsOn = ghosts;
      body.classList.toggle('mms-editing', on);
      if (on && !wired) {
        wired = true;
        addEventListener('resize', report);
        // VIS-73: a right press on a visitor control opens its editor in the portal (bridge event `menu`), not the browser's menu.
        // A place label answers its own right press first (labels.js: the place menu) and is left alone.
        document.addEventListener('contextmenu', (e) => {
          if (!ed || e.defaultPrevented) return;
          const id = controlAt(c.reg, e.target);
          if (!id) return;
          e.preventDefault();
          for (const fn of menus) fn({ control: id, screen: { x: Math.round(e.clientX), y: Math.round(e.clientY) } });
        });
        // while editing, a link of the interface opens nothing and "step inside" starts nothing
        document.addEventListener('click', (e) => { if (ed && e.target.closest?.('#chrome a[href], .credit-pop a[href], #credit-chip a[href], .step-inside, [data-control="card.booking"]')) { e.preventDefault(); e.stopPropagation(); } }, true);
      }
      if (!on) { target = null; sent = ''; outline(document, null); }
      if (redraw) { build(); labels.draw(); }
    },
    controlRects: rects,
    /** Outlines the control (or the control that owns the setting) being edited; null clears it. Answers its rectangle. */
    highlight(t) { target = t && controlOf(c.reg, t) ? t : null; const rect = ed ? targetRect() : null; outline(document, rect); return rect; },
    onControls(fn) { subs.add(fn); return () => subs.delete(fn); },
    onMenu(fn) { menus.add(fn); return () => menus.delete(fn); }, // VIS-73
    labelOf: labels.labelOf, // VIS-73: bridge `locate`
    // ---- WS-51 editor mode (end)
    /** WS-41 live season switch: the season shown now. Everything that reads the season or the season's content is
     *  drawn again: the switch and the dock's date chips, the browse list (content.seasonal titles and blurbs), the
     *  open card (off-season notice, its actions) and the trees popover (no snow row outside winter). */
    // fix/e2e-red-local: main.js calls this AFTER it has laid the season's content; the runtime's 'change' (above) drew
    // the list earlier, with the previous season's photos, and the same signature kept it from being drawn again
    setSeason(id) { contentRev++; time.setSeason(id); build(); },
    inset,
    /** How far right of the middle (px) a place the map flies to should land: a map style whose panels cover a side
     *  of a computer's screen (Dock's sheet and tools) says it (`c.aside`), so the place lands in the free part. */
    aside: () => (!c.phone && !c.layout.panel && c.aside?.()) || 0,
    isPlaying: time.isPlaying,
    setTouring: top.setTouring,
    setSun: time.setSun,
    syncTime: time.sync,
    step(dt) {
      time.step(dt);
      if (c.flash) { c.flash.t -= dt; if (c.flash.t <= 0) c.flash = null; }
    },
    frame(camera, controls, dt = 0.016) {
      column.turn(Math.atan2(camera.position.x - controls.target.x, camera.position.z - controls.target.z));
      labels.frame(camera, controls, dt);
    },
  };
}
